import Chart from 'chart.js/auto';
import React, { useEffect, useRef, useState } from 'react';
import { fetchHeatPumps } from '../../services/heatPumpService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

const formatSupabaseError = (err: unknown) => {
  if (!err || typeof err !== 'object') return 'Unknown error';
  const anyErr = err as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [anyErr.message, anyErr.details, anyErr.hint, anyErr.code].filter(Boolean);
  return parts.length ? parts.join(' | ') : 'Unknown error';
};

interface HeatPumpUnitsChartProps {
  location: string;
  geofips: number;
  onDataLoaded?: (rows: Record<string, unknown>[]) => void;
}

interface UnitsData {
  year: number;
  [key: string]: number; // Dynamic keys for different systems
}

export const HeatPumpUnitsChart: React.FC<HeatPumpUnitsChartProps> = ({ location, geofips, onDataLoaded }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onDataLoadedRef = useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;

  useEffect(() => {
    const fetchHeatPumpData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch data for the selected location
        const { data: locationData, error: locationError } = await fetchHeatPumps({
          select: 'year, system, units_installed',
          geofips
        });

        if (locationError) {
          console.error('Error fetching location heat pump data:', locationError);
          throw locationError;
        }

        if (!locationData || locationData.length === 0) {
          setError('No heat pump data available for this location.');
          return;
        }

        // Group data by year and system
        const dataByYear = new Map<number, Map<string, number>>();
        locationData?.forEach(record => {
          if (!dataByYear.has(record.year)) {
            dataByYear.set(record.year, new Map());
          }
          dataByYear.get(record.year)!.set(record.system, record.units_installed || 0);
        });

        // Get all unique systems
        const allSystems = new Set<string>();
        locationData?.forEach(record => {
          if (record.system) {
            allSystems.add(record.system);
          }
        });

        // Get all years
        const allYears = Array.from(dataByYear.keys()).sort();

        // Process data for chart
        const processedData: UnitsData[] = allYears.map(year => {
          const yearData: UnitsData = { year };
          const yearSystems = dataByYear.get(year) || new Map();
          
          allSystems.forEach(system => {
            yearData[system] = yearSystems.get(system) || 0;
          });

          return yearData;
        });

        onDataLoadedRef.current?.(processedData as Record<string, unknown>[]);

        // Create chart
        if (chartRef.current) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            const years = processedData.map(d => d.year);
            const systems = Array.from(allSystems);

            // Define colors for different systems
            const systemColors: { [key: string]: string } = {
              'HVAC': '#3b82f6',
              'HPWH': '#ef4444',
              'Heat Pump': '#10b981',
              'Air Source Heat Pump': '#f59e0b',
              'Ground Source Heat Pump': '#8b5cf6',
              'Water Heater': '#06b6d4',
              'Other': '#6b7280'
            };

            const datasets = systems.map(system => ({
              label: system,
              data: processedData.map(d => d[system] || 0),
              backgroundColor: systemColors[system] || '#6b7280',
              borderColor: systemColors[system] || '#6b7280',
              borderWidth: 1,
              stack: 'units'
            }));

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: years,
                datasets
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: standardTitle('Heat Pump Units Installed by Year'),
                  subtitle: {
                    display: true,
                    text: [
                      `${location} Heat Pump Installations`,
                      'Source: TECH Clean California Database',
                      'Note: Data reflects installations receiving TECH program incentives only, not all heat pump installations'
                    ],
                    font: {
                      size: 11
                    },
                    padding: {
                      bottom: 15
                    }
                  },
                  legend: standardLegend,
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.parsed?.y;
                        return `${context.dataset.label}: ${(value ?? 0).toLocaleString()} units`;
                      },
                      footer: (tooltipItems) => {
                        const total = tooltipItems.reduce((sum, item) => sum + (item.parsed?.y ?? 0), 0);
                        return `Total: ${total.toLocaleString()} units`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year'
                    },
                    stacked: true
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Units Installed'
                    },
                    beginAtZero: true,
                    stacked: true,
                    ticks: {
                      callback: (value) => Number(value).toLocaleString()
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching heat pump units data:', err);
        const message = err instanceof Error ? err.message : formatSupabaseError(err);
        console.error('HeatPumpUnitsChart Supabase error:', message);
        setError(`Failed to load heat pump units data: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchHeatPumpData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading heat pump units data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ height: '450px', width: '100%' }}>
        <canvas ref={chartRef} />
      </div>
      <div style={{ 
        marginTop: '10px', 
        fontSize: '11px', 
        fontStyle: 'italic', 
        color: '#666',
        textAlign: 'center'
      }}>
        <a 
          href="https://techcleanca.com/heat-pump-data/" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#3b82f6', textDecoration: 'underline' }}
        >
          TECH Clean California Database
        </a>
      </div>
    </div>
  );
};
